import React, { useEffect, useState } from 'react';
import { CreditCard, RefreshCw, Send, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { auth } from '../firebase';
import CardSetupModal from './CardSetupModal';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BRAND_ICONS = {
  Visa:       '💳 Visa',
  Mastercard: '💳 Mastercard',
  Amex:       '💳 Amex',
  Discover:   '💳 Discover',
  Jcb:        '💳 JCB',
  Diners:     '💳 Diners',
  Unionpay:   '💳 UnionPay',
  Unknown:    '💳',
};

function brandLabel(brand) {
  return BRAND_ICONS[brand] || `💳 ${brand || 'Card'}`;
}

function expLabel(month, year) {
  if (!month || !year) return '';
  return `Exp ${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

/**
 * CardOnFile — displays and manages the saved payment method for a lead.
 *
 * Props:
 *   companyId  (string)
 *   leadId     (string)
 *   leadPhone  (string) — used for optional SMS send
 */
export default function CardOnFile({ companyId, leadId, leadPhone }) {
  const [card, setCard]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [showModal, setShowModal]     = useState(false);

  // ── Fetch current card on file ─────────────────────────────────────────
  const fetchCard = async () => {
    if (!companyId || !leadId) return;
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res   = await fetch(
        `${API}/api/payments/card-on-file/${leadId}?companyId=${companyId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) return;
      const data = await res.json();
      setCard(data.status !== 'none' ? data : null);
    } catch {
      // silent — not a blocking failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCard(); }, [companyId, leadId]);   // eslint-disable-line

  // ── Path A: Send secure save-card link via SMS ─────────────────────────
  const handleSendLink = async () => {
    setSendingLink(true);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res   = await fetch(`${API}/api/payments/setup-link`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, leadId, sendSms: !!leadPhone }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.smsSent) {
          toast.success('Secure card link sent via SMS.');
        } else {
          // No phone — copy to clipboard
          navigator.clipboard.writeText(data.setupUrl)
            .then(() => toast.success('Setup link copied to clipboard.'))
            .catch(() => toast.info(`Link: ${data.setupUrl}`));
        }
        fetchCard(); // Refresh — status should now be 'pending'
      } else {
        toast.error(data.detail || 'Failed to create setup link');
      }
    } catch {
      toast.error('Failed to create setup link. Try again.');
    } finally {
      setSendingLink(false);
    }
  };

  // ── After Path B (staff modal) succeeds ───────────────────────────────
  const handleModalSuccess = (cardData) => {
    setCard(cardData);
    setShowModal(false);
    toast.success(`Card saved: ${cardData.brand} ••••${cardData.last4}`);
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Loading card status…
      </div>
    );
  }

  const hasActiveCard = card?.status === 'active';
  const isPending     = card?.status === 'pending';

  return (
    <>
      {/* ── Card info display ────────────────────────────────────────── */}
      {hasActiveCard && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3"
          data-testid="card-on-file-active"
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800 leading-tight">
                {brandLabel(card.brand)} ••••{card.last4}
              </p>
              <p className="text-xs text-green-600">
                {expLabel(card.expMonth, card.expYear)} · Card on file
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="text-xs shrink-0 border-green-300 text-green-700 hover:bg-green-100"
            onClick={handleSendLink}
            disabled={sendingLink}
            data-testid="replace-card-btn"
          >
            {sendingLink ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Replace'}
          </Button>
        </div>
      )}

      {isPending && !hasActiveCard && (
        <div
          className="flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3"
          data-testid="card-on-file-pending"
        >
          <CreditCard className="w-5 h-5 text-yellow-600 shrink-0" />
          <p className="text-sm text-yellow-800">Secure card link sent — awaiting client setup.</p>
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────────── */}
      {!hasActiveCard && (
        <div className="flex flex-wrap gap-2 mt-2">
          {/* Path A: Send secure link (client self-serves) */}
          <Button
            size="sm"
            variant="outline"
            className="text-xs gap-1"
            onClick={handleSendLink}
            disabled={sendingLink}
            data-testid="send-card-link-btn"
          >
            {sendingLink
              ? <RefreshCw className="w-3 h-3 animate-spin" />
              : <Send className="w-3 h-3" />}
            {sendingLink ? 'Sending…' : leadPhone ? 'Send Secure Card Link' : 'Copy Card Link'}
          </Button>

          {/* Path B: Staff-assisted card entry */}
          <Button
            size="sm"
            className="text-xs gap-1 bg-gray-900 hover:bg-gray-800 text-white"
            onClick={() => setShowModal(true)}
            data-testid="add-card-btn"
          >
            <Plus className="w-3 h-3" />
            Add Card on File
          </Button>
        </div>
      )}

      {/* Path B modal */}
      {showModal && (
        <CardSetupModal
          companyId={companyId}
          leadId={leadId}
          onSuccess={handleModalSuccess}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
